// Angular
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
// Libs
import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from 'primeng/dynamicdialog';
import { AgregarListaProductosUnitariaModalComponent } from '../../../../shared/components/agregar-lista-productos-unitaria-modal/agregar-lista-productos-unitaria-modal.component';
import { AgregarListaProductosMasivaModalComponent } from '../../../../shared/components/agregar-lista-productos-masiva-modal/agregar-lista-productos-masiva-modal.component';

import {
  ModalComponent,
  TipoIcon,
  TipoModal,
} from '../../../../shared/components/modal/modal.component';
import { RootService } from '../../../../shared/services/root.service';
import { EditarListaProductosComponent } from '../../../../shared/components/editar-lista-productos/editar-lista-productos.component';
import { SessionService } from '@core/services-v2/session/session.service';
import { ISession } from '@core/models-v2/auth/session.interface';
import { GeolocationServiceV2 } from '@core/services-v2/geolocation/geolocation.service';
import { ISelectedStore } from '@core/services-v2/geolocation/models/geolocation.interface';
import { WishlistApiService } from '@core/services-v2/wishlist/wishlist-api.service';
import {
  IProductWishlist,
  IWishlist,
} from '@core/services-v2/wishlist/models/wishlist-response.interface';
import { CartService } from '@core/services-v2/cart.service';
import { ScreenService } from '@shared/services/screen';

@Component({
  selector: 'app-page-listas-de-productos',
  templateUrl: './page-listas-de-productos.component.html',
  styleUrls: ['./page-listas-de-productos.component.scss'],
})
export class PageListasDeProductosComponent implements OnInit, OnDestroy {
  private readonly screenService = inject(ScreenService);

  isMobile = this.screenService.isMobile;

  visible: boolean = false;
  isWishlistModalVisible = false;
  newWishlistName = ''; // Nueva propiedad para el nombre
  usuario!: ISession;
  tiendaSeleccionada!: ISelectedStore;
  origen!: string[];
  addingToCart = false;
  showLoading = true;
  listas: IWishlist[] = [];
  listas_temp: IWishlist[] = [];
  listas_filtered: IWishlist[] = []; // Nueva propiedad para listas filtradas
  listSearchQuery: string = ''; // Nuevo filtro para listas

  // Propiedades para manejo de selección
  selectedProducts = new Set<string>();
  selectAllProducts = false;

  // Propiedad privada para almacenar el índice
  private _indexSelectedWishlist = 0;

  constructor(
    public rootService: RootService,
    private toastr: ToastrService,
    private modalService: BsModalService,
    private dialogService: DialogService,
    private readonly sessionService: SessionService,
    private readonly geolocationService: GeolocationServiceV2,
    private readonly wishlistApiService: WishlistApiService,
    public readonly cartService: CartService
  ) {}

  ngOnInit(): void {
    this.usuario = this.sessionService.getSession();
    this.tiendaSeleccionada = this.geolocationService.getSelectedStore();
    this.getWishlists();
    this.initializeSelection();
  }

  ngOnDestroy(): void {
    // Limpiar cualquier recurso si es necesario
    this.clearSelection();
  }

  /**
   * Obtener las listas de deseos.
   */
  private getWishlists(): void {
    this.listas = [];
    this.listas_temp = [];
    this.listas_filtered = [];
    this.showLoading = true;
    this.wishlistApiService.getWishlists(this.usuario.documentId).subscribe({
      next: (wishlists) => {
        this.listas = wishlists;
        this.listas_temp = this.listas;
        this.listas_filtered = this.listas; // Inicializar lista filtrada
        this.showLoading = false;
      },
      error: () => {
        this.showLoading = false;
      },
    });
  }

  /**
   * Buscar producto en una lista específica.
   * @param event
   * @param index
   */
  searchProductOnList(event: any, index: number): void {
    // Verificar que tenemos las listas temporales y el índice es válido
    if (
      !this.listas_temp ||
      !Array.isArray(this.listas_temp) ||
      !this.listas_temp[index]
    ) {
      return;
    }

    // Crear una copia profunda de las listas temporales
    this.listas = JSON.parse(JSON.stringify(this.listas_temp));

    // Verificar que la lista existe después de la copia
    if (!this.listas || !Array.isArray(this.listas) || !this.listas[index]) {
      return;
    }

    const currentList = this.listas[index];

    // Verificar que la lista tiene artículos
    if (
      !currentList ||
      !currentList.articles ||
      !Array.isArray(currentList.articles)
    ) {
      return;
    }

    const query = event?.target?.value?.toLowerCase() || '';

    // Filtrar los artículos
    currentList.articles = currentList.articles.filter((product) => {
      if (!product) return false;
      const name = product.name?.toLowerCase() || '';
      const sku = product.sku?.toLowerCase() || '';
      return name.includes(query) || sku.includes(query);
    });

    // También actualizar las listas filtradas si estamos trabajando con filtros
    if (
      this.listas_filtered &&
      Array.isArray(this.listas_filtered) &&
      this.listas_filtered[index]
    ) {
      this.listas_filtered[index] = currentList;
    }
  }

  /**
   * Cambiar nombre de una lista de deseos.
   * @param wishlist
   */
  changeWishlistName(wishlist: IWishlist): void {
    const bsModalRef = this.modalService.show(EditarListaProductosComponent, {
      initialState: {
        nombre: wishlist.name,
        closeToOK: false,
      },
    });
    bsModalRef.content?.event.subscribe(async (wishlistName: string) => {
      if (!wishlistName) return;
      this.wishlistApiService
        .updateWishlist({
          documentId: this.usuario.documentId,
          wishlistId: wishlist.id,
          name: wishlistName,
        })
        .subscribe({
          next: () => {
            this.toastr.success(`Lista actualizada exitosamente.`);
            this.getWishlists();
            bsModalRef.hide();
          },
        });
    });
  }

  deleteWishlist(wishlist: IWishlist): void {
    const bsModalRef = this.modalService.show(ModalComponent, {
      initialState: {
        titulo: 'Confirmación',
        mensaje: `¿Está seguro que desea <strong>eliminar</strong> la lista <strong>${wishlist.name}</strong>?`,
        tipoIcon: TipoIcon.QUESTION,
        tipoModal: TipoModal.QUESTION,
      },
    });
    bsModalRef.content?.event.subscribe(async (hasAccepted: boolean) => {
      if (!hasAccepted) return;
      this.wishlistApiService
        .deleteWishlist(this.usuario.documentId, wishlist.id)
        .subscribe({
          next: () => {
            this.toastr.success(`Lista eliminada exitosamente.`);
            this.getWishlists();
          },
        });
    });
  }

  /**
   * Eliminar un producto de una lista de deseos.
   * @param wishlist
   * @param sku
   */
  deleteProductFromWishlist(
    wishlist: IWishlist | undefined | null,
    sku: string
  ): void {
    if (!wishlist) {
      this.toastr.error('No hay una lista seleccionada.');
      return;
    }

    const bsModalRef = this.modalService.show(ModalComponent, {
      initialState: {
        titulo: 'Confirmación',
        mensaje: `¿Está seguro que desea <strong>eliminar</strong> el producto SKU <strong>${sku}</strong> de la lista <strong>${wishlist.name}</strong>?`,
        tipoIcon: TipoIcon.QUESTION,
        tipoModal: TipoModal.QUESTION,
      },
    });
    bsModalRef.content?.event.subscribe(async (hasAccepted: boolean) => {
      if (!hasAccepted || !wishlist) return;

      this.wishlistApiService
        .deleteProductFromWishlist({
          sku,
          wishlistId: wishlist.id,
          documentId: this.usuario.documentId,
        })
        .subscribe({
          next: () => {
            this.toastr.success('Producto eliminado exitosamente.');
            this.getWishlists();
          },
        });
    });
  }

  /**
   * Establecer lista de desesos como predeterminada.
   * @param wishlist
   */
  setDefaultWishlist(wishlist: IWishlist) {
    this.wishlistApiService
      .setDefaultWishlist(this.usuario.documentId, wishlist.id)
      .subscribe({
        next: () => this.getWishlists(),
      });
  }

  /**
   * Obtener el código de cliente de un producto (si es que tiene).
   * @param product
   * @returns
   */
  getCustomerCode(product: IProductWishlist): string {
    if (!product.customerCodes?.length) return '';
    const customerCode = product.customerCodes.find(
      (code) => code.documentId === this.usuario.documentId
    );
    return customerCode?.code || '';
  }

  createWishlist(): void {
    if (!this.newWishlistName.trim()) {
      this.toastr.error('El nombre de la lista es requerido.');
      return;
    }

    this.wishlistApiService
      .createWishlist({
        documentId: this.usuario.documentId,
        name: this.newWishlistName.trim(),
      })
      .subscribe({
        next: () => {
          this.isWishlistModalVisible = false;
          this.newWishlistName = ''; // Limpiar el campo
          this.toastr.success(`Lista creada exitosamente.`);
          this.getWishlists();
        },
        error: (error) => {
          this.toastr.error(`Error al crear la lista: ${error.error.message}`);
          this.isWishlistModalVisible = false;
          this.newWishlistName = ''; // Limpiar el campo en caso de error
        },
      });
  }

  /**
   * Filtrar listas por nombre - NUEVO MÉTODO
   * @param event
   */
  filterLists(event: any): void {
    const searchValue = event?.target?.value || '';
    this.listSearchQuery = searchValue.toLowerCase(); // Eliminar trim() para permitir espacios

    if (!this.listSearchQuery) {
      // Si no hay query, mostrar todas las listas
      this.listas_filtered = Array.isArray(this.listas)
        ? [...this.listas]
        : [];
    } else {
      // Filtrar listas por nombre - también buscar por espacios
      const listasArray = Array.isArray(this.listas) ? this.listas : [];
      this.listas_filtered = listasArray.filter((lista) => {
        if (!lista || typeof lista.name !== 'string') return false;
        return lista.name.toLowerCase().includes(this.listSearchQuery);
      });
    }

    // Ajustar el índice seleccionado si es necesario
    this.adjustSelectedIndex();
  }

  /**
   * Ajustar el índice seleccionado después del filtrado - NUEVO MÉTODO
   */
  private adjustSelectedIndex(): void {
    // Verificar que tenemos listas filtradas
    if (
      !this.listas_filtered ||
      !Array.isArray(this.listas_filtered) ||
      this.listas_filtered.length === 0
    ) {
      this._indexSelectedWishlist = 0;
      return;
    }

    // Buscar la lista seleccionada actual en las listas filtradas
    const currentSelectedList =
      Array.isArray(this.listas) && this.listas[this._indexSelectedWishlist]
        ? this.listas[this._indexSelectedWishlist]
        : null;

    if (currentSelectedList) {
      const newIndex = this.listas_filtered.findIndex(
        (lista) =>
          lista && currentSelectedList && lista.id === currentSelectedList.id
      );

      if (newIndex !== -1) {
        this._indexSelectedWishlist = newIndex;
      } else {
        // Si la lista seleccionada no está en los filtros, seleccionar la primera
        this._indexSelectedWishlist = 0;
      }
    } else {
      this._indexSelectedWishlist = 0;
    }

    this.clearSelection();
  }

  /**
   * Limpiar el filtro de listas - NUEVO MÉTODO
   */
  clearListFilter(): void {
    this.listSearchQuery = '';
    this.listas_filtered = Array.isArray(this.listas) ? [...this.listas] : [];
    this.adjustSelectedIndex();
  }

  /**
   * Abrir modal para agregar productos unitariamente a una lista
   */
  addProductsToWishlist(): void {
    // Verificar que hay una lista seleccionada
    const listaSeleccionada = this.currentSelectedList;
    if (!listaSeleccionada) {
      this.toastr.error(
        'No hay una lista seleccionada para agregar productos.'
      );
      return;
    }

    const modalConfig: any = {
      header: `Agregar productos`,
      modal: true,
      closable: true,
      closeOnEscape: true,
      dismissableMask: false,
      baseZIndex: 10000,
      data: {
        lista: listaSeleccionada,
      },
    };

    if (this.isMobile()) {
      // Configuración para móvil - pantalla completa
      modalConfig.width = '100vw';
      modalConfig.height = '100vh';
      modalConfig.maximizable = false;
      modalConfig.resizable = false;
      modalConfig.draggable = false;
      modalConfig.position = 'center';
      modalConfig.contentStyle = {
        padding: '0',
        'overflow-y': 'hidden',
        'border-radius': '0',
        margin: '0',
        'max-height': '100vh',
        height: '100vh',
      };
      modalConfig.style = {
        'border-radius': '0',
        margin: '0',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100vh',
        'max-width': '100vw',
        'max-height': '100vh',
        position: 'fixed',
      };
    } else {
      // Configuración para desktop
      modalConfig.width = '90vw';
      modalConfig.height = '90vh';
      modalConfig.maximizable = true;
      modalConfig.resizable = true;
      modalConfig.draggable = true;
      modalConfig.contentStyle = {
        padding: '0',
        'overflow-y': 'hidden',
      };
    }

    modalConfig.breakpoints = {
      '960px': this.isMobile() ? '100vw' : '95vw',
      '768px': '100vw',
      '640px': '100vw',
    };

    const ref = this.dialogService.open(
      AgregarListaProductosUnitariaModalComponent,
      modalConfig
    );

    ref.onClose.subscribe((hasChanges) => {
      if (hasChanges) {
        this.getWishlists();
        this.toastr.success(
          `Productos agregados exitosamente a "${listaSeleccionada.name}".`
        );
      }
    });
  }

  /**
   * Abrir modal para carga masiva desde archivo - usar el nuevo modal de PrimeNG
   */
  addProductsFromFileToWishlist(): void {
    // Verificar que hay una lista seleccionada
    const listaSeleccionada = this.currentSelectedList;
    if (!listaSeleccionada) {
      this.toastr.error(
        'No hay una lista seleccionada para agregar productos.'
      );
      return;
    }

    const modalConfig: any = {
      header: `Carga masiva`,
      modal: true,
      closable: true,
      closeOnEscape: true,
      dismissableMask: false,
      baseZIndex: 10000,
      data: {
        lista: listaSeleccionada, // Pasar la lista seleccionada
      },
    };

    if (this.isMobile()) {
      // Configuración para móvil - pantalla completa
      modalConfig.width = '100vw';
      modalConfig.height = '100vh';
      modalConfig.maximizable = false;
      modalConfig.resizable = false;
      modalConfig.draggable = false;
      modalConfig.position = 'center';
      modalConfig.contentStyle = {
        padding: '0',
        'overflow-y': 'hidden',
        'border-radius': '0',
        margin: '0',
        'max-height': '100vh',
        height: '100vh',
      };
      modalConfig.style = {
        'border-radius': '0',
        margin: '0',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100vh',
        'max-width': '100vw',
        'max-height': '100vh',
        position: 'fixed',
      };
    } else {
      // Configuración para desktop
      modalConfig.width = '90vw';
      modalConfig.height = '90vh';
      modalConfig.maximizable = true;
      modalConfig.resizable = true;
      modalConfig.draggable = true;
      modalConfig.contentStyle = {
        padding: '0',
        'overflow-y': 'hidden',
      };
    }

    modalConfig.breakpoints = {
      '960px': this.isMobile() ? '100vw' : '95vw',
      '768px': '100vw',
      '640px': '100vw',
    };

    const ref = this.dialogService.open(
      AgregarListaProductosMasivaModalComponent,
      modalConfig
    );

    ref.onClose.subscribe((hasChanges) => {
      if (hasChanges) {
        this.getWishlists();
        this.toastr.success(
          `Productos agregados exitosamente a "${listaSeleccionada.name}".`
        );
      }
    });
  }

  addtoCart(item: any): void {
    if (this.addingToCart) {
      return;
    }
    this.addingToCart = true;

    const itemToAdd: any = [
      {
        sku: item.sku,
        name: item.name,
        origin: null,
        images: item.images,
        quantity: 1,
        price: 0,
      },
    ];

    this.cartService.addLista(itemToAdd).subscribe({
      next: () => {
        this.addingToCart = false;
        this.toastr.success(
          `Producto ${item.sku} agregado al carro correctamente.`
        );
      },
      error: (error) => {
        this.addingToCart = false;
        this.toastr.error('Hubo un error al agregar el producto al carro.');
        console.error('Error al agregar al carro:', error);
      },
    });
  }

  prepareItemsForCart(articles: any[]): any[] {
    return articles.map((article) => ({
      sku: article.sku,
      name: article.name,
      origin: null,
      images: [article.preview],
      quantity: 1,
    }));
  }

  getMenuItems(lista: IWishlist | undefined) {
    if (!lista) return [];

    return [
      {
        label: 'Establecer como principal',
        icon: 'pi pi-star',
        command: () => this.setDefaultWishlist(lista),
      },
      {
        separator: true,
      },
      {
        label: 'Cambiar nombre',
        icon: 'pi pi-pencil',
        command: () => this.changeWishlistName(lista),
      },
      {
        label: 'Eliminar lista',
        icon: 'pi pi-trash',
        styleClass: 'text-red-500',
        command: () => this.deleteWishlist(lista),
      },
    ];
  }

  /**
   * Inicializa el estado de selección cuando cambia la lista
   */
  private initializeSelection(): void {
    this.clearSelection();
  }

  /**
   * Verifica si un producto está seleccionado
   */
  isProductSelected(product: any): boolean {
    return this.selectedProducts.has(product.sku);
  }

  /**
   * Establece el estado de selección de un producto
   */
  setProductSelection(product: any, selected: boolean): void {
    if (selected) {
      this.selectedProducts.add(product.sku);
    } else {
      this.selectedProducts.delete(product.sku);
    }
    this.updateSelectAllState();
  }

  /**
   * Maneja el cambio de selección de un producto individual
   */
  onProductSelectionChange(product: any, selected: boolean): void {
    this.setProductSelection(product, selected);
  }

  /**
   * Selecciona o deselecciona todos los productos
   */
  toggleSelectAll(): void {
    const currentList = this.currentSelectedList;
    if (!currentList?.articles) return;

    this.selectedProducts.clear();

    if (this.selectAllProducts) {
      currentList.articles.forEach((product) => {
        this.selectedProducts.add(product.sku);
      });
    }
  }

  /**
   * Actualiza el estado del checkbox "Seleccionar todos"
   */
  private updateSelectAllState(): void {
    const currentList = this.currentSelectedList;
    if (!currentList?.articles) return;

    const totalProducts = currentList.articles.length;
    const selectedCount = this.selectedProducts.size;

    this.selectAllProducts =
      selectedCount === totalProducts && totalProducts > 0;
  }

  /**
   * Obtiene el número de productos seleccionados
   */
  getSelectedProductsCount(): number {
    return this.selectedProducts.size;
  }

  /**
   * Método para mostrar tips o ayuda (futuro)
   */
  showHelp(): void {
    // Implementación futura para mostrar ayuda o tips
    console.log('Mostrando ayuda para listas de productos');
  }

  /**
   * Limpia toda la selección
   */
  clearSelection(): void {
    this.selectedProducts.clear();
    this.selectAllProducts = false;
  }

  /**
   * Agrega los productos seleccionados al carrito
   */
  addSelectedToCart(): void {
    if (this.addingToCart) {
      return;
    }
    this.addingToCart = true;

    const currentList = this.currentSelectedList;
    if (!currentList?.articles) return;

    const selectedProductsList = currentList.articles.filter((product) =>
      this.selectedProducts.has(product.sku)
    );

    console.log('selectedProducts: ', this.selectedProducts);
    console.log('selectedProductsList: ', selectedProductsList);

    // FIXME:
    const itemsToAdd = this.prepareItemsForCart(selectedProductsList);
    console.log('itemsToAdd: ', itemsToAdd);

    this.cartService.addLista(itemsToAdd).subscribe({
      next: () => {
        // Limpiar selección después de agregar al carrito
        this.clearSelection();
        this.addingToCart = false;
        this.toastr.success('Productos agregados al carrito correctamente.');
      },
      error: (error) => {
        this.addingToCart = false;
        this.toastr.error(
          'Hubo un error al agregar los productos al carrito.'
        );
        console.error('Error al agregar al carrito:', error);
      },
    });
  }

  /**
   * Maneja el cambio de lista seleccionada
   */
  onWishlistChange(): void {
    this.clearSelection();
  }

  // Sobrescribir el método original para limpiar selección al cambiar de lista
  set indexSelectedWishlist(value: number) {
    this._indexSelectedWishlist = value;
    this.clearSelection();
  }

  get indexSelectedWishlist(): number {
    return this._indexSelectedWishlist;
  }

  /**
   * Obtener la lista seleccionada actual - NUEVO MÉTODO HELPER
   */
  get currentSelectedList(): IWishlist | undefined {
    if (
      !this.listas_filtered ||
      !Array.isArray(this.listas_filtered) ||
      this.listas_filtered.length === 0
    ) {
      return undefined;
    }

    // Asegurar que el índice esté dentro del rango válido
    const index = Math.max(
      0,
      Math.min(this._indexSelectedWishlist, this.listas_filtered.length - 1)
    );
    return this.listas_filtered[index];
  }

  /**
   * Obtiene el total de productos en todas las listas
   */
  getTotalProducts(): number {
    if (!Array.isArray(this.listas)) return 0;

    return this.listas.reduce((total, lista) => {
      if (!lista || !Array.isArray(lista.articles)) return total;
      return total + lista.articles.length;
    }, 0);
  }
}
